import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Bell, Check, Trash2, UserPlus, MessageCircle, Sparkles } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";

const ICON_FOR = {
  friend_request: UserPlus,
  friend_accepted: UserPlus,
  message: MessageCircle,
};

function relTime(iso) {
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function Notifications() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);

  const load = async () => {
    const r = await api.get("/notifications");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };
  const remove = async (id) => {
    await api.delete(`/notifications/${id}`);
    load();
  };
  const markAll = async () => {
    await api.post("/notifications/read-all");
    toast.success("All caught up");
    load();
  };

  const open = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.link) nav(n.link);
  };

  return (
    <PhoneFrame>
      <div className="px-5 pt-6 pb-10">
        <div className="flex items-center justify-between">
          <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="notif-back">
            <ChevronLeft size={16} /> Back
          </button>
          {items.some((i) => !i.read) && (
            <button data-testid="notif-mark-all" onClick={markAll} className="text-xs text-[#2fbf71] font-semibold">
              Mark all read
            </button>
          )}
        </div>
        <h1 className="ps-heading text-3xl font-bold mt-4">Notifications</h1>
        <p className="text-slate-400 text-sm">Friend requests, messages, and coach insights.</p>

        {items.length === 0 ? (
          <div className="mt-12 text-center text-slate-500">
            <Bell size={28} className="mx-auto opacity-50" />
            <div className="mt-3 text-sm">You're all caught up.</div>
          </div>
        ) : (
          <div className="mt-6 space-y-2">
            {items.map((n) => {
              const Icon = ICON_FOR[n.type] || Sparkles;
              return (
                <div
                  key={n.id}
                  data-testid={`notif-${n.id}`}
                  className={`ps-card p-4 flex gap-3 items-start ${!n.read ? "border-[#2fbf71]/30" : ""}`}
                >
                  <div className="w-10 h-10 rounded-2xl bg-[#142534] flex items-center justify-center shrink-0">
                    <Icon size={18} color="#2fbf71" />
                  </div>
                  <button onClick={() => open(n)} className="flex-1 text-left">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-semibold text-sm">{n.title}</div>
                      <div className="text-[10px] text-slate-500 shrink-0">{relTime(n.created_at)}</div>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{n.body}</div>
                  </button>
                  <div className="flex flex-col gap-1">
                    {!n.read && (
                      <button data-testid={`notif-read-${n.id}`} onClick={() => markRead(n.id)} className="p-1.5 rounded-lg text-[#2fbf71] hover:bg-[#142534]">
                        <Check size={14} />
                      </button>
                    )}
                    <button data-testid={`notif-del-${n.id}`} onClick={() => remove(n.id)} className="p-1.5 rounded-lg text-slate-500 hover:bg-[#142534]">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PhoneFrame>
  );
}
